import type { SankeyLink as D3SankeyLink } from 'd3-sankey';

import type {
  SankeyLink,
  SankeyNode,
  SankeyNodeValidated,
  SankeyScale,
} from './types';

export const MIN_LINK_WIDTH = 2;
const MAX_FLOOR_SHARE = 0.05;

export const solveMinLinkValue = ({
  pxPerValue,
  maxLinkValue,
}: {
  pxPerValue: number;
  maxLinkValue: number;
}): number => {
  if (pxPerValue <= 0 || maxLinkValue <= 0) return 0;
  return Math.min(MIN_LINK_WIDTH / pxPerValue, maxLinkValue * MAX_FLOOR_SHARE);
};

export const floorLinkValue = (rawValue: number, minValue: number): number =>
  rawValue > 0 ? Math.max(rawValue, minValue) : 0;

export const LINEAR_SANKEY_SCALE: SankeyScale = {
  totalExponent: 1,
  shareExponent: 1,
};

export const isLinearSankeyScale = (scale: SankeyScale): boolean =>
  scale.totalExponent === 1 && scale.shareExponent === 1;

export const scaleLinkValues = ({
  values,
  sideKeys,
  scale,
}: {
  values: number[];
  sideKeys: (string | undefined)[];
  scale: SankeyScale;
}): number[] => {
  const indexesBySide = new Map<string, number[]>();
  sideKeys.forEach((key, index) => {
    if (key === undefined) return;
    const group = indexesBySide.get(key);
    if (group) group.push(index);
    else indexesBySide.set(key, [index]);
  });

  const scaled = values.map(() => 0);
  for (const indexes of indexesBySide.values()) {
    const sideValues = indexes.map((index) => Math.max(values[index] ?? 0, 0));
    const sideTotal = sideValues.reduce((sum, value) => sum + value, 0);
    if (sideTotal <= 0) continue;

    const weights = sideValues.map(
      (value) => (value / sideTotal) ** scale.shareExponent
    );
    const weightTotal = weights.reduce((sum, weight) => sum + weight, 0);
    if (weightTotal <= 0) continue;

    const scaledTotal = sideTotal ** scale.totalExponent;
    indexes.forEach((index, position) => {
      scaled[index] = (scaledTotal * (weights[position] ?? 0)) / weightTotal;
    });
  }
  return scaled;
};

const nodeCenterY = (node: SankeyNodeValidated): number =>
  (node.y0 + node.y1) / 2;

/** Per-link attachment Y at each end, keyed by link index. */
export type SankeyLinkEndpoints = {
  sourceY: Map<number, number>;
  targetY: Map<number, number>;
};

export const EMPTY_LINK_ENDPOINTS: SankeyLinkEndpoints = {
  sourceY: new Map(),
  targetY: new Map(),
};

const MAX_INTERIOR_LABEL_ABOVE = 40;
const INTERIOR_LABEL_ABOVE_TAPER = 16;

const interiorGapFor = (count: number, labelReserve: number): number => {
  const above = Math.max(
    0,
    MAX_INTERIOR_LABEL_ABOVE - (count - 2) * INTERIOR_LABEL_ABOVE_TAPER
  );
  return labelReserve + above;
};

export const interiorLabelScale = ({
  nodes,
  maxDepth,
  plotHeight,
  labelReserve,
}: {
  nodes: SankeyNodeValidated[];
  maxDepth: number;
  plotHeight: number;
  labelReserve: number;
}): number => {
  if (labelReserve <= 0 || maxDepth < 2) return 1;
  const totalByDepth = new Map<number, number>();
  const countByDepth = new Map<number, number>();
  for (const node of nodes) {
    if (node.depth === 0 || node.depth === maxDepth) continue;
    totalByDepth.set(
      node.depth,
      (totalByDepth.get(node.depth) ?? 0) + (node.y1 - node.y0)
    );
    countByDepth.set(node.depth, (countByDepth.get(node.depth) ?? 0) + 1);
  }
  let scale = 1;
  for (const [depth, totalHeight] of totalByDepth) {
    const count = countByDepth.get(depth) ?? 1;
    if (count < 2 || totalHeight <= 0) continue;
    const gap = interiorGapFor(count, labelReserve);
    const available = plotHeight - (count - 1) * gap;
    if (available <= 0) continue;
    scale = Math.min(scale, available / totalHeight);
  }
  return Math.max(scale, 0.4);
};

/** Re-space a column to fill the plot height (lone node centered). */
const distributeColumn = (
  columnNodes: SankeyNodeValidated[],
  top: number,
  plotHeight: number
): void => {
  if (columnNodes.length === 0) return;
  const ordered = [...columnNodes].sort((a, b) => a.y0 - b.y0);
  const heights = ordered.map((node) => node.y1 - node.y0);
  const totalHeight = heights.reduce((sum, h) => sum + h, 0);
  const slack = Math.max(0, plotHeight - totalHeight);
  const gap = ordered.length > 1 ? slack / (ordered.length - 1) : 0;

  let cursor = ordered.length === 1 ? top + slack / 2 : top;
  ordered.forEach((node, i) => {
    const height = heights[i] ?? 0;
    node.y0 = cursor;
    node.y1 = cursor + height;
    cursor += height + gap;
  });
};

/** Spread one side's ribbons across the node bar, ordered by partner Y. */
const distributeSide = (
  node: SankeyNodeValidated,
  links: D3SankeyLink<SankeyNode, SankeyLink>[],
  partner: (link: D3SankeyLink<SankeyNode, SankeyLink>) => SankeyNodeValidated,
  out: Map<number, number>
): void => {
  if (links.length === 0) return;
  const ordered = [...links].sort(
    (a, b) => nodeCenterY(partner(a)) - nodeCenterY(partner(b))
  );

  let cursor = node.y0;
  ordered.forEach((link) => {
    const width = link.width ?? 0;
    if (link.index !== undefined) {
      out.set(link.index, cursor + width / 2);
    }
    cursor += width;
  });
};

/** Fill every column to the plot height and resolve each ribbon's attachment Y. */
export const applyColumnFillLayout = ({
  nodes,
  top,
  plotHeight,
}: {
  nodes: SankeyNodeValidated[];
  top: number;
  plotHeight: number;
}): SankeyLinkEndpoints => {
  const byDepth = new Map<number, SankeyNodeValidated[]>();
  for (const node of nodes) {
    const column = byDepth.get(node.depth);
    if (column) {
      column.push(node);
    } else {
      byDepth.set(node.depth, [node]);
    }
  }
  byDepth.forEach((column) => {
    distributeColumn(column, top, plotHeight);
  });

  // Endpoints come from the final node positions — no link dragging needed.
  const sourceY = new Map<number, number>();
  const targetY = new Map<number, number>();
  for (const node of nodes) {
    distributeSide(
      node,
      node.sourceLinks ?? [],
      (link) => link.target as SankeyNodeValidated,
      sourceY
    );
    distributeSide(
      node,
      node.targetLinks ?? [],
      (link) => link.source as SankeyNodeValidated,
      targetY
    );
  }
  return { sourceY, targetY };
};
